import { useCallback, useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { Loader2, Pencil, Search } from "lucide-react"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { ADMIN_EMAIL } from "@/lib/constants"
import {
  addDomainToVercel,
  removeDomainFromVercel,
  ROOT_DOMAIN,
} from "@/lib/vercel-domains"
import {
  checkSubdomainAvailability,
  validateSubdomain,
} from "@/lib/subdomainRules"
import { supabase } from "@/lib/supabase"
import { queuePublicCachePurge } from "@/lib/publicCache"
import { useAuthStore } from "@/stores/authStore"

type OrgRow = { id: string; name: string; slug: string }
type HistoryRow = {
  id: string
  organization_id: string
  old_subdomain: string | null
  new_subdomain: string
  changed_at: string
}

export function SubdomainManagementPage() {
  const { user } = useAuthStore()
  const navigate = useNavigate()
  const [orgs, setOrgs] = useState<OrgRow[]>([])
  const [history, setHistory] = useState<HistoryRow[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState("")
  const [editOrg, setEditOrg] = useState<OrgRow | null>(null)
  const [editSlug, setEditSlug] = useState("")
  const [editError, setEditError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  const fetchData = useCallback(async () => {
    const [orgsRes, historyRes] = await Promise.all([
      supabase.from("organizations").select("id, name, slug").order("name"),
      supabase
        .from("subdomain_history")
        .select("id, organization_id, old_subdomain, new_subdomain, changed_at")
        .order("changed_at", { ascending: false })
        .limit(50),
    ])
    if (orgsRes.error) {
      toast.error(orgsRes.error.message)
      return
    }
    if (historyRes.error) {
      toast.error(historyRes.error.message)
      return
    }
    setOrgs((orgsRes.data ?? []) as OrgRow[])
    setHistory((historyRes.data ?? []) as HistoryRow[])
  }, [])

  useEffect(() => {
    const email = user?.email ?? null
    if (email === null) return
    if (email !== ADMIN_EMAIL) {
      toast.error("Unauthorized")
      navigate("/dashboard", { replace: true })
      return
    }
    setLoading(true)
    fetchData().finally(() => setLoading(false))
  }, [user?.email, navigate, fetchData])

  const filteredOrgs = orgs.filter(
    (o) =>
      o.name.toLowerCase().includes(search.toLowerCase()) ||
      o.slug.toLowerCase().includes(search.toLowerCase())
  )

  const openEdit = (org: OrgRow) => {
    setEditOrg(org)
    setEditSlug(org.slug)
    setEditError(null)
  }

  const validateEdit = useCallback((): string | null => {
    const normalized = editSlug.trim().toLowerCase()
    const format = validateSubdomain(normalized)
    if (!format.valid) return format.error ?? "Invalid format"
    return null
  }, [editSlug])

  const handleSaveEdit = async () => {
    if (!editOrg) return
    const newSlug = editSlug.trim().toLowerCase()
    if (newSlug === editOrg.slug) {
      setEditOrg(null)
      return
    }
    const err = validateEdit()
    if (err) {
      setEditError(err)
      return
    }
    const available = await checkSubdomainAvailability(newSlug, editOrg.id)
    if (!available) {
      setEditError("This subdomain is already taken")
      return
    }
    setSaving(true)
    setEditError(null)
    try {
      if (editOrg.slug) {
        const removeResult = await removeDomainFromVercel(editOrg.slug, editOrg.id)
        if (!removeResult.success && removeResult.error) {
          toast.error(removeResult.error)
          setSaving(false)
          return
        }
      }
      const addResult = await addDomainToVercel(newSlug, editOrg.id)
      if (!addResult.success) {
        toast.error(addResult.error ?? "Failed to add domain to Vercel")
        setSaving(false)
        return
      }
      const { error: updateError } = await supabase
        .from("organizations")
        .update({ slug: newSlug, updated_at: new Date().toISOString() })
        .eq("id", editOrg.id)
      if (updateError) {
        toast.error(updateError.message)
        setSaving(false)
        return
      }
      queuePublicCachePurge(editOrg.id, { type: "domain-change" })
      await supabase.from("subdomain_history").insert({
        organization_id: editOrg.id,
        old_subdomain: editOrg.slug || null,
        new_subdomain: newSlug,
        changed_by: user?.id ?? null,
      })
      await fetchData()
      setEditOrg(null)
      toast.success(`Subdomain updated to ${newSlug}.${ROOT_DOMAIN}`)
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to update")
    } finally {
      setSaving(false)
    }
  }

  if (user?.email !== ADMIN_EMAIL) return null

  if (loading && orgs.length === 0) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <Loader2 className="h-10 w-10 animate-spin text-primary" aria-hidden />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold">Subdomain management</h2>
        <p className="text-sm text-muted-foreground">
          View and edit organization subdomains (platform admin only).
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Organizations</CardTitle>
          <CardDescription>
            All orgs and their website subdomains. Edit to change subdomain
            (bypasses normal cooldown).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="relative max-w-sm">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search by org name or subdomain..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <div className="overflow-auto rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Organization</TableHead>
                  <TableHead>Subdomain</TableHead>
                  <TableHead className="w-[100px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredOrgs.map((org) => (
                  <TableRow key={org.id}>
                    <TableCell className="font-medium">{org.name}</TableCell>
                    <TableCell className="font-mono text-sm">
                      {org.slug || "—"}.{ROOT_DOMAIN}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openEdit(org)}
                      >
                        <Pencil className="mr-1 h-3 w-3" />
                        Edit
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Audit log</CardTitle>
          <CardDescription>
            Recent subdomain changes (subdomain_history).
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-auto rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Org ID</TableHead>
                  <TableHead>Old</TableHead>
                  <TableHead>New</TableHead>
                  <TableHead>When</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {history.map((h) => (
                  <TableRow key={h.id}>
                    <TableCell className="font-mono text-xs">
                      {h.organization_id.slice(0, 8)}…
                    </TableCell>
                    <TableCell className="font-mono text-sm">
                      {h.old_subdomain ?? "—"}
                    </TableCell>
                    <TableCell className="font-mono text-sm">
                      {h.new_subdomain}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {new Date(h.changed_at).toLocaleString()}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={!!editOrg} onOpenChange={(open) => !open && setEditOrg(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit subdomain</DialogTitle>
            <DialogDescription>
              Change subdomain for {editOrg?.name}. Old URL will stop working.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="admin-edit-slug">New subdomain</Label>
            <div className="flex items-center gap-2">
              <Input
                id="admin-edit-slug"
                value={editSlug}
                onChange={(e) => {
                  setEditSlug(e.target.value.trim().toLowerCase())
                  setEditError(null)
                }}
                placeholder="new-slug"
                className="font-mono"
              />
              <span className="text-sm text-muted-foreground">.{ROOT_DOMAIN}</span>
            </div>
            {editError && (
              <p className="text-sm text-destructive">{editError}</p>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEditOrg(null)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button onClick={handleSaveEdit} disabled={saving}>
              {saving ? "Configuring DNS…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
